<script lang="ts">
  import ConfigureConnectionGrid from "$lib/ui/configure-connection-grid/grid.svelte";
  import { dataSources } from "$lib/utils/constants";

  import { push, querystring } from "svelte-spa-router";
  import OnboardingWizardShell from "$lib/components/onboarding/OnboardingWizardShell.svelte";

  import { selectedOrgStore } from "$stores/orgs";
  import { userStore } from "$stores/user";
  import InviteAdminForm from "$lib/components/integrations/InviteAdminFlyoverV2.svelte";
  import SuccessfulFlyover from "$lib/components/integrations/SuccessfullConnectionFlyover.svelte";

  import { onDestroy } from "svelte";
  import { makeApiRequest } from "$lib/utils/api";
  import DataSourceFlyoverOAuth from "$lib/components/integrations/DataSourceFlyoverOAuth.svelte";
  import DataSourceFlyoverDelegate from "$lib/components/integrations/DataSourceFlyoverDelegate.svelte";
  import SuccessfulInviteFlyover from "$lib/components/integrations/SuccessfulInviteFlyover.svelte";
  import { sidebar } from "$lib/stores/sidebar.svelte";
  import { environment } from "$lib/utils/env";
  import { encodeFullPageRedirectState, fullPageRedirectState } from "$lib/utils/fullPageRedirects";
  import { derived } from "svelte/store";
  import ConnectionPendingFlyover from "$lib/components/integrations/ConnectionPendingFlyover.svelte";
  import DataSourceConfigurationFlyover from "$lib/components/integrations/DataSourceConfigurationFlyover.svelte";
  import { toaster } from "$lib/utils/toaster";

  let selectedProvider = $state(dataSources.sharepoint);
  let emailLength = $state(0);
  let isConnectingProvider = $state<boolean>(false);

  // Compute allowed domains: use org domains if available, otherwise derive from user's email (for onboarding)
  function getAllowedDomains(): string[] {
    const orgDomains = $selectedOrgStore?.domains;
    if (orgDomains && orgDomains.length > 0) {
      return orgDomains;
    }
    // During onboarding, derive from user's email
    const userEmail = $userStore?.email;
    if (userEmail && userEmail.includes("@")) {
      const userDomain = userEmail.split("@")[1];
      if (userDomain) {
        return [userDomain];
      }
    }
    return [];
  }

  async function handleEmailSubmit({ emails }: { emails: string[] }) {
    emailLength = emails.length;
    let dataSources = $selectedOrgStore?.dataSources || [];
    let dataSource = dataSources.find((o) => o.provider === selectedProvider.id);

    let admins = Object.fromEntries([
      ...(dataSource?.invitedAdmins || []).map((email) => [email, false]),
      ...emails.map((email) => [email, true]),
    ]);

    try {
      await makeApiRequest(`${$selectedOrgStore?.resourcePrefix}/`, {
        data_sources: [
          {
            provider: selectedProvider.id,
            admins: admins,
          },
        ],
      });
      push(`${$selectedOrgStore?.pagePrefix}/connect_data?action=inviteSuccess&provider=${selectedProvider.id}`);
    } catch (error) {
      console.error("Error sending invites:", error);
    }
  }

  async function initiateSharepointAdminConsent(scopes: string[]) {
    const state = encodeFullPageRedirectState(
      `${$selectedOrgStore?.pagePrefix}/connect_data?action=connectPending&provider=sharepoint`,
      {},
    );
    // https://learn.microsoft.com/en-us/entra/identity-platform/v2-admin-consent
    const url = new URL("https://login.microsoftonline.com/organizations/v2.0/adminconsent");

    url.searchParams.set("client_id", (await environment).sharepointAppClientId);
    url.searchParams.set("scope", ".default");
    url.searchParams.set("redirect_uri", window.location.protocol + "//" + window.location.host);
    url.searchParams.set("state", state);

    console.log("Redirecting to Sharepoint admin consent endpoint", url.toString());
    window.location.assign(url);
  }

  async function initiateGoogleAdminConsent(scopes: string[]) {
    const state = encodeFullPageRedirectState(
      `${$selectedOrgStore?.pagePrefix}/connect_data?action=connectPending&provider=gdrive`,
      {},
    );
    // https://developers.google.com/identity/protocols/oauth2
    const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");

    url.searchParams.set("client_id", (await environment).googleAppClientId);
    url.searchParams.set("scope", scopes.join(" "));
    url.searchParams.set("redirect_uri", window.location.protocol + "//" + window.location.host);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("state", state);

    console.log("Redirecting to Google admin consent endpoint", url.toString());
    window.location.assign(url);
  }

  async function onAdminConnect(provider: string, scopes: string[]) {
    if (provider.toLowerCase() == "sharepoint") {
      await initiateSharepointAdminConsent(scopes);
    }
    if (provider.toLowerCase() == "gdrive") {
      await initiateGoogleAdminConsent(scopes);
    }
  }

  async function onVerifyDelegation(provider: string, scopes: string[], adminEmail: string) {
    console.log("Verifying delegation for provider", provider);
    console.log("Scopes", scopes);
    console.log("Admin email", adminEmail);
    const org = $selectedOrgStore;

    if (!adminEmail) {
      toaster.error("Unable to connect to provider", "No admin email found");
      return;
    }

    if (!org) {
      console.error("No org id found");
      toaster.error("Unable to connect to provider", "No org id found");
      return;
    }

    // Validate email domain against organization's registered domains (or user's domain during onboarding)
    const allowedDomains = getAllowedDomains();
    if (allowedDomains.length > 0) {
      const emailDomain = adminEmail.split("@")[1];
      if (!emailDomain || !allowedDomains.includes(emailDomain)) {
        toaster.error(
          "Invalid email domain",
          `Admin email domain must match your organization's domain (${allowedDomains.join(", ")})`,
        );
        return;
      }
    }

    if (provider.toLowerCase() == "gdrive") {
      const data: { data_sources: any } = { data_sources: [] };
      const newDataSource: { provider: string; scopes: string[]; admin_email: string } = {
        provider: provider,
        admin_email: adminEmail,
        scopes: scopes,
      };
      data.data_sources.push(newDataSource);
      try {
        await makeApiRequest(`${org.resourcePrefix}/validate-gdrive`, newDataSource, "POST");
        await makeApiRequest(`${org.resourcePrefix}/`, data, "PUT");
        push(`${org.pagePrefix}/connect_data?action=connectSuccess&provider=${selectedProvider.id}`);
      } catch (error) {
        console.error("Error connecting to provider:", error);
        if (error instanceof Error) {
          toaster.error("Unable to connect to provider", error.message);
        } else if (typeof error === "string") {
          toaster.error("Unable to connect to provider", error);
        } else {
          toaster.error("Unable to connect to provider", "Unknown error");
        }
      }
    } else {
      toaster.error("Unable to connect to provider", "Unknown Provider");
    }
  }

  const unsubFromQueryString = derived([querystring, selectedOrgStore], (b) => b).subscribe(async ([qs, org]) => {
    const sp = new URLSearchParams(qs);
    const providerId = sp.get("provider");
    if (providerId && providerId in dataSources) {
      selectedProvider = dataSources[providerId as keyof typeof dataSources];
    }
    switch (sp.get("action")) {
      case "connect":
        sidebar.snippet = DataConsent;
        break;
      case "invite":
        sidebar.snippet = InviteAdmin;
        break;
      case "connectPending":
        sidebar.snippet = ConnectionPending;
        if (org && !isConnectingProvider) {
          try {
            isConnectingProvider = true;
            const redirectState = await fullPageRedirectState;
            if (redirectState.decodedState) {
              const data: { data_sources: any } = { data_sources: [] };
              const newDataSource: { provider: string; [key: string]: string } = {
                provider: selectedProvider.id,
              };

              if (providerId === "sharepoint") {
                if (!redirectState.params.get("tenant")) {
                  console.error("Tenant ID is required for Sharepoint");
                } else {
                  newDataSource["tenant_id"] = redirectState.params.get("tenant")!;
                }
              } else if (providerId === "gdrive") {
                if (!redirectState.params.get("domain")) {
                  console.error("domain is required for Google Drive");
                } else {
                  newDataSource["domain"] = redirectState.params.get("domain")!;
                }
              }

              data.data_sources.push(newDataSource);
              await makeApiRequest(`${org.resourcePrefix}/`, data, "PUT");
              window.history.replaceState(null, "", window.location.pathname);
              // TODO: hard-coded to sharepoint - need to update everything to use id
              push(
                `${$selectedOrgStore?.pagePrefix}/connect_data?action=connectSuccess&provider=${selectedProvider.id}`,
              );
            }
          } finally {
            isConnectingProvider = false;
          }
        }
        break;
      case "connectSuccess":
        sidebar.snippet = ConnectSuccess;
        break;
      case "inviteSuccess":
        sidebar.snippet = InviteSuccess;
        break;
      case "configure":
        sidebar.snippet = ConfigurationFlyover;
        break;
      default:
        sidebar.snippet = null;
        break;
    }
  });

  onDestroy(() => {
    unsubFromQueryString();
  });
</script>

{#snippet DataConsent()}
  {#if ["gdrive"].includes(selectedProvider.id)}
    <DataSourceFlyoverDelegate
      provider={selectedProvider}
      onAdminInvite={() => {
        sidebar.snippet = InviteAdmin;
      }}
      {onVerifyDelegation}
      allowedDomains={getAllowedDomains()}
    />
  {:else}
    <DataSourceFlyoverOAuth
      provider={selectedProvider}
      onAdminInvite={() => {
        sidebar.snippet = InviteAdmin;
      }}
      {onAdminConnect}
    />
  {/if}
{/snippet}

{#snippet InviteAdmin()}
  <InviteAdminForm connectionId={selectedProvider.name} onSubmit={handleEmailSubmit} />
{/snippet}

{#snippet ConnectSuccess()}
  <SuccessfulFlyover
    connectionId={selectedProvider.name}
    successActionButtonText={`Configure ${selectedProvider.name}`}
    successAction={() => push(`${$selectedOrgStore?.pagePrefix}/connect_data?action=configure&provider=sharepoint`)}
  />
{/snippet}

{#snippet InviteSuccess()}
  <SuccessfulInviteFlyover connectionId={selectedProvider.name} emailsLength={emailLength} />
{/snippet}

{#snippet ConnectionPending()}
  <ConnectionPendingFlyover connectionId={selectedProvider.name} />
{/snippet}

{#snippet ConfigurationFlyover()}
  <DataSourceConfigurationFlyover providerName={selectedProvider.name} providerId={selectedProvider.id} />
{/snippet}

<OnboardingWizardShell
  currentStep={1}
  onNext={() => push(`${$selectedOrgStore?.pagePrefix}/review`)}
  onBack={() => push(`${$selectedOrgStore?.pagePrefix}/connect_sso`)}
>
  <h2 class="text-3xl font-semibold text-offblack mb-3">Connect to your Data Source providers</h2>
  <p class="font-semibold text-offblack">
    You will need an admin from your organization to connect your data providers
  </p>
  <div class="relative inset-0 flex items-center my-3" aria-hidden="true">
    <div class="w-full border-t border-gray-300"></div>
  </div>
  <ConfigureConnectionGrid
    items={Object.entries(dataSources).map(([k, v]) => v)}
    connected={$selectedOrgStore?.dataSources.filter((ds) => ds.connected).map((ds) => ds.provider) ?? []}
    pending={$selectedOrgStore?.dataSources.filter((ds) => !ds.connected).map((ds) => ds.provider) ?? []}
    onConnect={(item) => {
      // TODO: hard-coded to sharepoint - need to update everything to use id
      push(`${$selectedOrgStore?.pagePrefix}/connect_data?action=connect&provider=${item.id}`);
    }}
    onEdit={(item) => {
      push(`${$selectedOrgStore?.pagePrefix}/connect_data?action=configure&provider=${item.id}`);
    }}
  />
</OnboardingWizardShell>
